import { DatePicker, FormField, Select, todayIso, useToast } from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { formErrors } from '../../../lib/errors';
import { useLocations, useMoveColonies } from '../api';

export interface MoveDialogProps {
  open: boolean;
  onClose: () => void;
  colonyIds: string[];
  /** Location the colonies stand at now (excluded as target if all share it). */
  currentLocationId?: string;
  onMoved?: () => void;
}

/** Relocation of one or more colonies (LOC-006). */
export function MoveDialog({
  open,
  onClose,
  colonyIds,
  currentLocationId,
  onMoved,
}: MoveDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const locations = useLocations(false, open);
  const move = useMoveColonies();
  const targets = (locations.data ?? []).filter((l) => l.id !== currentLocationId);
  const [target, setTarget] = useState('');
  const [on, setOn] = useState(todayIso());
  const targetValue = target || targets[0]?.id || '';
  const errors = formErrors(move.error, { on: ['colony.relocation_before_last'] });
  const close = () => {
    move.reset();
    onClose();
  };
  return (
    <FormDialog
      open={open}
      onClose={close}
      title={t('apiary.move.title')}
      description={t('apiary.move.description', { count: colonyIds.length })}
      size="sm"
      submitLabel={t('apiary.move.submit')}
      pending={move.isPending}
      error={errors.general}
      onSubmit={() =>
        move.mutate(
          { colony_ids: colonyIds, location_id: targetValue, on },
          {
            onSuccess: (r) => {
              toast.show(t('apiary.move.moved', { count: r.colony_ids.length }));
              onMoved?.();
              close();
            },
          },
        )
      }
    >
      <FormField label={t('apiary.move.target')} required>
        {(p) => (
          <Select
            {...p}
            value={targetValue}
            onChange={(e) => setTarget(e.target.value)}
            options={targets.map((l) => ({ value: l.id, label: l.name }))}
          />
        )}
      </FormField>
      <FormField label={t('apiary.move.date')} error={errors.field.on}>
        {(p) => <DatePicker {...p} value={on} max={todayIso()} onChange={setOn} />}
      </FormField>
    </FormDialog>
  );
}
